/**
 * Sessions — auto-save, recent tracking, named sessions, and workspace clipboard for Unveil.
 * Pure localStorage. No backend required.
 */

import { getWorkspaceText, clearWorkspace } from './workspace.js';
import { applyTextToPrimaryInput } from './utils/input-targets.js';
import { safeReadJSON, safeSetItem, safeRemoveItem } from './utils/storage.js';
import { escapeHtml } from './utils/html.js';
import { timeAgo } from './utils/date-format.js';

const AUTOSAVE_PREFIX = 'unveil:autosave:';
const SESSIONS_KEY = 'unveil:sessions';
const RECENT_KEY = 'unveil:recent';
const MAX_RECENT = 10;

// ─── State capture / restore ──────────────────────────────────────────────────

function getPanel(toolId) {
  return document.querySelector(`.tool-panel[data-tool-panel="${toolId}"]`);
}

/**
 * Secrets are never stored or shared: password fields and anything inside
 * [data-no-persist] (keys, passphrases) stay on screen only, as do chosen files. A hidden input is
 * state only when marked data-persist — a value the tool sets itself, such as
 * Geomancy's cast — and is restored with an input event like any other field.
 */
function isPersistable(el) {
  // A chosen file is never saved (and a file input's value cannot be set back)
  if (el.type === 'password' || el.type === 'file' || el.closest('[data-no-persist]')) return false;
  return el.type !== 'hidden' || el.hasAttribute('data-persist');
}

/*
 * Modes chosen with buttons are part of the state too:
 *   <button id="x-mode" data-persist-mode="decrypt">   a toggle; the tool keeps the attribute
 *                                                      equal to the current mode
 *   <div id="x-tabs" data-persist-choice>               a group of buttons with data-choice,
 *     <button data-choice="a" aria-pressed="true">      the chosen one aria-pressed="true"
 *                                                      (aria-selected="true" in a role="tablist")
 * A mode alone is not "content": it travels with an input, never instead of one —
 * unless the choice is the content itself (data-persist-choice="content", e.g. the
 * exhibit chosen in the Cipher Archive).
 */
function modeRank(id) {
  const el = document.getElementById(id);
  if (!el) return 0;
  if (el.hasAttribute('data-persist-choice')) return 2;
  return el.hasAttribute('data-persist-mode') ? 1 : 0;
}

/** Page order: of two choice groups, the one above (the tabs) is restored first. */
function pageOrder(idA, idB) {
  const a = document.getElementById(idA);
  const b = document.getElementById(idB);
  if (!a || !b || a === b) return 0;
  return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}

const CHOSEN = '[data-choice][aria-pressed="true"], [data-choice][aria-selected="true"]';

function captureModes(panelEl, state) {
  panelEl.querySelectorAll('[data-persist-mode][id]').forEach((btn) => {
    state[btn.id] = btn.dataset.persistMode;
  });
  panelEl.querySelectorAll('[data-persist-choice][id]').forEach((group) => {
    const chosen = group.querySelector(CHOSEN);
    if (chosen) state[group.id] = chosen.dataset.choice;
  });
}

/** Sets a button mode or choice; true when it changed. */
function restoreMode(el, value) {
  if (el.hasAttribute('data-persist-mode')) {
    // A toggle steps through its modes: click until it shows the saved one
    for (let i = 0; i < 4 && el.dataset.persistMode !== value; i++) el.click();
    return el.dataset.persistMode === value;
  }
  const choice = [...el.querySelectorAll('[data-choice]')].find((b) => b.dataset.choice === value);
  if (!choice || choice.matches(CHOSEN)) return false;
  choice.click();
  return true;
}

function captureState(panelEl) {
  if (!panelEl) return null;
  const state = {};
  let hasContent = false;

  panelEl.querySelectorAll('input[id], textarea[id], select[id]').forEach((el) => {
    if (!isPersistable(el)) return;
    if (el.type === 'checkbox' || el.type === 'radio') {
      state[el.id] = el.checked;
      if (el.checked) hasContent = true;
    } else if (el.value !== '') {
      state[el.id] = el.value;
      hasContent = true;
    }
  });

  if (panelEl.querySelector('[data-persist-choice="content"] [data-choice][aria-pressed="true"]')) hasContent = true;
  if (hasContent) captureModes(panelEl, state);
  return hasContent ? state : null;
}

/** Each panel's fields as its tool first set them, before anything was restored: { id: value }. */
const mountDefaults = new WeakMap();

/** Remembers a freshly mounted tool's fields, so a whole state can later put back the ones it leaves out. */
export function rememberDefaults(toolId) {
  const panel = getPanel(toolId);
  if (!panel || mountDefaults.has(panel)) return;
  const defaults = {};
  panel.querySelectorAll('input[id], textarea[id], select[id]').forEach((el) => {
    if (!isPersistable(el)) return;
    defaults[el.id] = el.type === 'checkbox' || el.type === 'radio' ? el.checked : el.value;
  });
  mountDefaults.set(panel, defaults);
}

/**
 * A whole state (a share link, a saved session) leaves out only what was
 * empty when it was taken; in a panel already open, the fields it leaves out
 * go back to their first values, so it opens as it would in a fresh one.
 */
function resetUnlisted(panelEl, state) {
  const defaults = mountDefaults.get(panelEl);
  if (!defaults) return false;
  let changed = false;
  for (const [id, value] of Object.entries(defaults)) {
    if (id in state) continue;
    const el = document.getElementById(id);
    if (!el || !panelEl.contains(el)) continue;
    if (el.type === 'checkbox' || el.type === 'radio') {
      if (el.checked === value) continue;
      el.checked = value;
      el.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      if (el.value === value) continue;
      el.value = value;
      el.dispatchEvent(new Event(el.tagName === 'SELECT' ? 'change' : 'input', { bubbles: true }));
    }
    changed = true;
  }
  return changed;
}

function restoreState(panelEl, state, { whole = false } = {}) {
  if (!panelEl || !state) return false;
  let restored = whole && resetUnlisted(panelEl, state);

  // Tabs first (a toggle can mean something different on each tab), then toggles,
  // then the inputs, so they are read in the mode they were written in; choice
  // groups in page order, so a tab's own mode pills follow the tab
  const entries = Object.entries(state).sort(([a], [b]) => modeRank(b) - modeRank(a) || pageOrder(a, b));
  for (const [id, value] of entries) {
    const el = document.getElementById(id);
    if (!el || !panelEl.contains(el)) continue;
    if (el.hasAttribute('data-persist-mode') || el.hasAttribute('data-persist-choice')) {
      if (typeof value === 'string' && restoreMode(el, value)) restored = true;
      continue;
    }
    if (!isPersistable(el)) continue;

    if (el.tagName === 'SELECT') {
      // A saved choice the list no longer offers (renamed or removed) is skipped
      const offered = [...el.options].some((o) => o.value === String(value));
      if (offered && el.value !== String(value)) {
        el.value = value;
        el.dispatchEvent(new Event('change', { bubbles: true }));
        restored = true;
      }
    } else if (el.type === 'checkbox' || el.type === 'radio') {
      if (el.checked !== value) {
        el.checked = value;
        el.dispatchEvent(new Event('change', { bubbles: true }));
        restored = true;
      }
    } else {
      if (el.value !== String(value)) {
        el.value = value;
        el.dispatchEvent(new Event('input', { bubbles: true }));
        restored = true;
      }
    }
  }

  return restored;
}

// ─── Auto-save / restore ─────────────────────────────────────────────────────

export function autoSave(toolId) {
  const panel = getPanel(toolId);
  const state = captureState(panel);
  if (state) {
    safeSetItem(
      AUTOSAVE_PREFIX + toolId,
      JSON.stringify(state),
      'Autosave failed. Local storage may be full.',
    );
  }
}

/** The tool's current input for a share link, or null when there is none. */
export function captureShareState(toolId) {
  return captureState(getPanel(toolId));
}

/** A saved state with retired field ids given their current ones ({ oldId: newId }). */
export function renameStateFields(state, fields) {
  if (!state || !fields || !Object.keys(fields).length) return state;
  return Object.fromEntries(Object.entries(state).map(([id, value]) => [fields[id] || id, value]));
}

/**
 * A saved state brought up to the tool as it is now: old field ids renamed
 * (`renamedFields`), a choice whose value was renamed given its new one
 * (`renamedChoices`, { groupId: { oldChoice: newChoice } }, or { to, also }
 * where the old choice also meant other fields, set unless the state has
 * them), and a choice group the tool gained later set to what a state saved
 * before it means (`addedChoices`, { groupId: choice }) — so an older link
 * opens on the tab its input belongs to.
 */
export function upgradeState(state, { renamedFields, renamedChoices, addedChoices } = {}) {
  let renamed = renameStateFields(state, renamedFields);
  if (renamed && renamedChoices) {
    renamed = { ...renamed };
    for (const [id, values] of Object.entries(renamedChoices)) {
      if (typeof renamed[id] !== 'string' || !Object.hasOwn(values, renamed[id])) continue;
      const rule = values[renamed[id]];
      const { to, also = {} } = typeof rule === 'string' ? { to: rule } : rule;
      renamed[id] = to;
      for (const [field, value] of Object.entries(also)) if (!(field in renamed)) renamed[field] = value;
    }
  }
  return renamed && addedChoices ? { ...addedChoices, ...renamed } : renamed;
}

/**
 * Restores a shared or saved state, else this device's autosave (brought up
 * to date by `upgradeState`). `whole`: the state is all of a tool's input (a
 * share link, a saved session), not a few fields another tool sets.
 */
export function autoRestore(toolId, overrideState, toolEntry, { whole = false } = {}) {
  const panel = getPanel(toolId);
  if (!panel) return false;
  if (overrideState) return restoreState(panel, overrideState, { whole });

  const state = safeReadJSON(AUTOSAVE_PREFIX + toolId, null);
  if (!state) return false;
  return restoreState(panel, upgradeState(state, toolEntry));
}

// ─── Pending restore (for loading saved sessions) ────────────────────────────

let _pendingRestore = null;

/** A state for the tool opened next; `whole` when it is all of the tool's input (a share link, a saved session). */
export function setPendingRestore(state, { whole = false } = {}) {
  _pendingRestore = state ? { state, whole } : null;
}

/** The pending state and whether it is whole: { state, whole }, or null. */
export function consumePendingRestore() {
  const p = _pendingRestore;
  _pendingRestore = null;
  return p;
}

// ─── Recent tracking ─────────────────────────────────────────────────────────

export function trackRecent(toolId, toolLabel) {
  let recent = safeReadJSON(RECENT_KEY, []);
  recent = recent.filter((r) => r.id !== toolId);
  recent.unshift({ id: toolId, label: toolLabel, ts: Date.now() });
  if (recent.length > MAX_RECENT) recent.length = MAX_RECENT;
  safeSetItem(RECENT_KEY, JSON.stringify(recent), 'Recent history could not be updated.');
}

function getRecent() {
  return safeReadJSON(RECENT_KEY, []);
}

// ─── Named sessions ─────────────────────────────────────────────────────────

function uid() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

function getIndex() {
  return safeReadJSON(SESSIONS_KEY, []);
}

function setIndex(arr) {
  return safeSetItem(SESSIONS_KEY, JSON.stringify(arr), 'Session data could not be saved.');
}

function saveSession(toolId, toolLabel, name) {
  const panel = getPanel(toolId);
  const state = captureState(panel);
  if (!state) return null;

  const session = { id: uid(), name, toolId, toolLabel, ts: Date.now(), state };
  const index = getIndex();
  index.unshift(session);
  return setIndex(index) ? session : null;
}

function deleteSession(sessionId) {
  setIndex(getIndex().filter((s) => s.id !== sessionId));
}

// ─── Time formatting ─────────────────────────────────────────────────────────

// ─── Drawer UI ───────────────────────────────────────────────────────────────

let _navigate, _getActiveTool, _getActiveToolLabel;
let _drawer;
let _previousFocus = null;
let _closeNotebookFn = null;

function renderDrawer() {
  const content = _drawer.querySelector('.ses-content');
  const recent = getRecent();
  const sessions = getIndex();
  const wksText = getWorkspaceText();

  const wksSection = `
    <div class="ses-section">
      <h3 class="ses-section-title">Workspace</h3>
      ${wksText
        ? `<div class="wks-card">
            <div class="wks-preview">${escapeHtml(wksText)}</div>
            <div class="wks-actions">
              <button class="wks-btn" data-action="wks-use">Use</button>
              <button class="wks-btn wks-btn--danger" data-action="wks-clear">Clear</button>
            </div>
          </div>`
        : '<p class="ses-empty">Click "Clip" on any result to save text here</p>'}
    </div>`;

  const recentItems = recent.length
    ? recent
        .map(
          (r) => `
      <button class="ses-item" data-action="go" data-tool="${r.id}">
        <span class="ses-item-name">${r.label}</span>
        <span class="ses-item-time">${timeAgo(r.ts)}</span>
      </button>`,
        )
        .join('')
    : '<p class="ses-empty">No recent activity</p>';

  const savedItems = sessions.length
    ? sessions
        .map(
          (s) => `
      <div class="ses-item ses-item--saved">
        <button class="ses-item-main" data-action="load" data-sid="${s.id}">
          <span class="ses-item-name">${escapeHtml(s.name)}</span>
          <span class="ses-item-meta">${s.toolLabel} · ${timeAgo(s.ts)}</span>
        </button>
        <button class="ses-item-del" data-action="del" data-sid="${s.id}" aria-label="Delete">✕</button>
      </div>`,
        )
        .join('')
    : '<p class="ses-empty">No saved sessions</p>';

  content.innerHTML = `
    ${wksSection}
    <div class="ses-section">
      <h3 class="ses-section-title">Save Current</h3>
      <div class="ses-save-row">
        <input type="text" class="ses-save-input" id="ses-name" placeholder="Session name..." autocomplete="off" />
        <button class="ses-save-btn" id="ses-do-save">Save</button>
      </div>
    </div>
    <div class="ses-section">
      <h3 class="ses-section-title">Recent</h3>
      <div class="ses-list">${recentItems}</div>
    </div>
    <div class="ses-section">
      <h3 class="ses-section-title">Saved</h3>
      <div class="ses-list">${savedItems}</div>
    </div>`;
}

function trapFocus(e) {
  if (e.key !== 'Tab') return;
  const panel = _drawer.querySelector('.ses-panel');
  const focusable = [...panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')];
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (e.shiftKey && document.activeElement === first) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && document.activeElement === last) {
    e.preventDefault();
    first.focus();
  }
}

function open() {
  if (_closeNotebookFn) _closeNotebookFn();
  _previousFocus = document.activeElement;
  renderDrawer();
  _drawer.classList.add('is-open');
  document.body.style.overflow = 'hidden';
  _drawer.querySelector('.ses-close')?.focus();
  _drawer.addEventListener('keydown', trapFocus);
}

function close() {
  _drawer.classList.remove('is-open');
  document.body.style.overflow = '';
  _drawer.removeEventListener('keydown', trapFocus);
  _previousFocus?.focus();
}

export function closeSessions() {
  if (_drawer?.classList.contains('is-open')) close();
}

export function initSessions({ navigate, getActiveTool, getActiveToolLabel, closeNotebookFn }) {
  // Before 2026-10 the analyzed password was autosaved; remove any stored copy.
  safeRemoveItem(AUTOSAVE_PREFIX + 'passwordanalyzer');
  _navigate = navigate;
  _getActiveTool = getActiveTool;
  _getActiveToolLabel = getActiveToolLabel;
  _closeNotebookFn = closeNotebookFn;

  // Build drawer
  _drawer = document.createElement('div');
  _drawer.className = 'ses-drawer';
  _drawer.setAttribute('role', 'dialog');
  _drawer.setAttribute('aria-modal', 'true');
  _drawer.setAttribute('aria-label', 'Sessions');
  _drawer.innerHTML = `
    <div class="ses-backdrop"></div>
    <div class="ses-panel">
      <div class="ses-header">
        <h2 class="ses-title">Sessions</h2>
        <button class="ses-close" aria-label="Close">✕</button>
      </div>
      <div class="ses-content"></div>
    </div>`;
  document.body.appendChild(_drawer);

  // Open trigger
  document.getElementById('sessions-open-btn')?.addEventListener('click', open);

  // Close triggers
  _drawer.querySelector('.ses-backdrop').addEventListener('click', close);
  _drawer.querySelector('.ses-close').addEventListener('click', close);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && _drawer.classList.contains('is-open')) close();
  });

  // Delegated actions
  _drawer.addEventListener('click', (e) => {
    const el = e.target.closest('[data-action]');
    if (!el) return;

    if (el.dataset.action === 'go') {
      close();
      _navigate(el.dataset.tool);
    }

    if (el.dataset.action === 'load') {
      const sessions = getIndex();
      const session = sessions.find((s) => s.id === el.dataset.sid);
      if (session) {
        close();
        setPendingRestore(session.state, { whole: true });
        _navigate(session.toolId);
      }
    }

    if (el.dataset.action === 'del') {
      e.stopPropagation();
      deleteSession(el.dataset.sid);
      renderDrawer();
    }

    if (el.dataset.action === 'wks-use') {
      const text = getWorkspaceText();
      if (!text) return;
      close();
      const panel = [...document.querySelectorAll('.tool-panel')].find(
        (p) => p.style.display !== 'none',
      );
      if (!panel) return;
      applyTextToPrimaryInput(panel, text, { focus: true });
    }

    if (el.dataset.action === 'wks-clear') {
      clearWorkspace();
      renderDrawer();
    }
  });

  // Save handler
  _drawer.addEventListener('click', (e) => {
    if (e.target.id !== 'ses-do-save') return;
    const nameInput = document.getElementById('ses-name');
    const name = nameInput?.value?.trim() || _getActiveToolLabel();
    const toolId = _getActiveTool();
    const toolLabel = _getActiveToolLabel();
    if (toolId && saveSession(toolId, toolLabel, name)) {
      nameInput.value = '';
      renderDrawer();
    }
  });

  _drawer.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && e.target.id === 'ses-name') {
      document.getElementById('ses-do-save')?.click();
    }
  });
}
